/**
 * ChatMessage — polymorphic renderer for one message. Branches on `role`:
 *   - user      → right-aligned bubble
 *   - assistant → DocBlock with AuthorHeader, ToolStatusInline, MarkdownRenderer,
 *                 inline figures, ReasoningToggle, optional StreamingCursor
 *   - system    → muted info row
 *   - error     → error pane with optional retry hint
 *
 * The streaming cursor only appears when this message is the actively
 * streaming one (matches chatStore.streamingMessageId). Subscribing to
 * the signal here keeps the cursor flicker-free without re-rendering the
 * entire message list.
 */

import { streamingMessageId, openFeedback } from '../../state/chatStore.js'
import { canGiveIonFeedback } from '../../state/userState.js'
import { AuthorHeader } from './AuthorHeader.jsx'
import { DocBlock } from './DocBlock.jsx'
import { MarkdownRenderer } from './MarkdownRenderer.jsx'
import { StreamingCursor } from './StreamingCursor.jsx'
import { IonWorkingCue } from './IonWorkingCue.jsx'
import { IonQuotaCard } from './IonQuotaCard.jsx'
import { t } from '../../i18n/index.js'

/**
 * @param {object} props
 * @param {object} props.message — { id, role, content, sources, toolCalls, kpiGroups, figures, status, createdAt, error }
 * @param {number} [props.assistantIndex] — 0-based ordinal among assistant
 *   messages; used as the feedback target handle (stable across live/hydrated).
 */
export function ChatMessage({ message, assistantIndex }) {
  if (!message) return null

  switch (message.role) {
    case 'user':
      return <UserMessage message={message} />
    case 'assistant':
      return <AssistantMessage message={message} assistantIndex={assistantIndex} />
    case 'system':
      return <SystemMessage message={message} />
    case 'error':
      return <ErrorMessage message={message} />
    default:
      return null
  }
}

function UserMessage({ message }) {
  // Timestamp is stored as ISO-8601 (UTC); render in the user's local
  // timezone using the browser's locale-aware formatter — same helper
  // the assistant message uses, so the two read consistently.
  const time = formatLocalTime(message.createdAt)
  return (
    <div class="ion-msg ion-msg--user">
      <div class="ion-msg__bubble">
        {message.content}
        {message.meta?.example && (
          <span class="chat-message__example-chip">{t('chat.message.exampleLabel')}</span>
        )}
      </div>
      {time && (
        <time class="ion-msg__time" dateTime={message.createdAt}>
          {time}
        </time>
      )}
    </div>
  )
}

function formatLocalTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function AssistantMessage({ message, assistantIndex }) {
  const isStreaming = streamingMessageId.value === message.id
  const isAborted = message.status === 'aborted'
  const isError = message.status === 'error'
  // A blinking text caret means "typing next character imminently". While a
  // tool runs no text arrives for seconds, so a caret there reads as frozen —
  // hide it and let the animated working cue be the sole activity signal.
  const toolRunning = (message.toolCalls || []).some((tc) => tc.status === 'running')
  // Per-turn feedback affordance for admins and users with feedback_access —
  // hidden while still streaming (the turn isn't final yet, and its ordinal
  // could still shift).
  const showFeedback =
    canGiveIonFeedback() && !isStreaming && typeof assistantIndex === 'number'

  return (
    <div class="ion-msg ion-msg--assistant">
      <DocBlock streaming={isStreaming} aborted={isAborted} error={isError}>
        <AuthorHeader />

        {/* No top-of-message tool-status pill. Each tool call renders
            inline at the position the assistant emitted it — see
            MarkdownRenderer's [[TOOL:id]] hydration. The inline
            marker carries running/done/error states, so a duplicate
            top pill produced a "tool was at the top then jumped
            inline when done" perception. Position is now stable from
            start to finish. */}

        <div class="ion-msg__body">
          <MarkdownRenderer
            content={message.content}
            sources={message.sources}
            kpiGroups={message.kpiGroups}
            toolCalls={message.toolCalls}
          />
          {isStreaming && !toolRunning && <StreamingCursor />}
        </div>

        {/* Single, quiet "working" cue while streaming — replaces the per-tool
            chatter (the generic InlineToolMarker pills are now admin-only).
            While a tool runs this is the ONLY activity signal (caret hidden),
            so it carries a step-aware label + a live elapsed counter so a long
            wait reads as active, not stuck. See IonWorkingCue. */}
        {isStreaming && <IonWorkingCue toolCalls={message.toolCalls} />}

        {message.figures && message.figures.length > 0 && (
          <div class="ion-msg__figures">
            {message.figures.map((fig, idx) => (
              <figure key={idx} class="ion-msg__figure">
                {fig.url && (
                  <img
                    src={fig.url}
                    alt={fig.alt || fig.caption || ''}
                    width={fig.width || undefined}
                    height={fig.height || undefined}
                    loading="lazy"
                  />
                )}
                {fig.caption && (
                  <figcaption class="ion-msg__figcaption">
                    {fig.caption}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        )}

        {isAborted && (
          <p class="ion-msg__abort-note">{t('ion.message.aborted')}</p>
        )}

        {isError && message.error && (
          message.error.code === 'quota_exceeded' ? (
            <IonQuotaCard error={message.error} />
          ) : (
            <p class="ion-msg__error-note" role="alert">
              {message.error.message || t('ion.message.error.generic')}
            </p>
          )
        )}

        {/* No tools-used footer at message tail — the inline tool
            markers in the prose (and ToolStatusInline above the body
            during streaming) carry that signal. A trailing list would
            be redundant noise. */}

        {showFeedback && (
          <div class="ion-msg__actions">
            <button
              type="button"
              class="ion-msg__feedback-btn"
              aria-label={t('ion.feedback.message.aria')}
              title={t('ion.feedback.message.aria')}
              onClick={() =>
                openFeedback({ scope: 'message', targetTurnIndex: assistantIndex })
              }
            >
              <FeedbackIcon />
            </button>
          </div>
        )}
      </DocBlock>
    </div>
  )
}

function SystemMessage({ message }) {
  return (
    <div class="ion-msg ion-msg--system" role="note">
      {message.content}
    </div>
  )
}

function ErrorMessage({ message }) {
  return (
    <div class="ion-msg ion-msg--error" role="alert">
      <strong class="ion-msg__error-title">{t('ion.message.error.title')}</strong>
      <span class="ion-msg__error-text">{message.content}</span>
    </div>
  )
}

function FeedbackIcon() {
  // Flag — "report / leave feedback on this answer" (consistent with header).
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <line x1="4" y1="22" x2="4" y2="15" />
    </svg>
  )
}
